<template>
    <span ref="select" v-click-outside="hide"
          :class="{
                'select2 select2-container select2-container--default select2-container--above': true,
                'select2-container--disabled': disabled,
                'select2-container--focus select2-container--open': showed
          }" @click="toggleSelect">
        <span class="selection">
            <span class="select2-selection select2-selection--single" >
                <span class="select2-selection__rendered">
                    <span v-if="canBeEmpty && (selectedValue || $slots.optionContent)" class="select2-selection__clear" @click.stop="$emit('update:modelValue', null)">×</span>
                    <slot name="optionContent" v-if="selectedValue || $slots.optionContent">{{ selectedValue }}</slot>
                    <span v-else class="select2-selection__placeholder">{{ placeholder }}</span>
                </span>
                    <span class="select2-selection__arrow" role="presentation">
                    <b role="presentation"></b>
                </span>
            </span>
        </span>
        <span class="dropdown-wrapper" aria-hidden="true"></span>
    </span>

    <Teleport to="body" v-if="showed">
        <span
            ref="options"
            @click.stop
            :class="{
                'select2-container select2-container--default': true,
                'select2-container--open': showed,
            }"
            :style="{ 'z-index': zIndex, position: 'absolute' }"
        >
        <span class="select2-dropdown select2-dropdown--above">
            <span class="select2-search select2-search--dropdown" v-if="hasSearch">
                <input class="select2-search__field" ref="searchInput" v-model="search" @input="$emit('update:search', search)" :placeholder="translate('alerts.enter_some_characters')">
            </span>
            <span class="select2-results">
                <ul class="select2-results__options" aria-expanded="true" aria-hidden="false" ref="list">
                    <slot name="additionalOption" :hide="hide"></slot>
                    <li
                        :class="{
                            'select2-results__option': true,
                            'select2-results__option--highlighted': index === highlightedIndex
                        }"
                        :aria-selected="modelValue === findByPath(option, optionKey)"
                        :aria-disabled="selectedValues.includes(findByPath(option, optionKey))"
                        v-for="(option, index) in options"
                        :title="optionTitleFormatter ? optionTitleFormatter(option) : findByPath(option, optionValue)"
                        @mouseover="highlightedIndex = index"
                        @click.stop="!selectedValues.includes(findByPath(option, optionKey)) ? updateModelValue(option) : null"
                    >
                        <slot name="optionItem" :option="option" :index="index">
                            {{ findByPath(option, optionValue) }}
                        </slot>
                    </li>
                    <li v-if="options.length === 0" class="select2-results__option select2-results__message" >{{translate('alerts.no_records_found')}}</li>
                    <slot name="additionalLi"></slot>
                </ul>
            </span>
            <div v-if="hasCreateButton" class="options-footer">
                <AlphaBackgroundButton color="teal" @click="$emit('click:create')">
                    {{ translate('buttons.create') }}
                </AlphaBackgroundButton>
            </div>
        </span>
        </span>
    </Teleport>
</template>
<script>
import vClickOutside from 'click-outside-vue3'
import { createPopper } from '@popperjs/core';
import AlphaBackgroundButton from '../../button/AlphaBackgroundButton.vue';
export default {
    name: "AppSelect",
    props: {
        modelValue: {
            type: [String, Number],
            default: ''
        },
        selectedValue: {
            type: [String, Number],
            default: ''
        },
        placeholder: {
            type: String,
            default: () => translate('components.select.default_placeholder')
        },
        options: {
            type: Array,
            default: () => []
        },
        optionKey: {
            type: String,
            default: 'uuid'
        },
        optionValue: {
            type: String,
            default: 'name'
        },
        canBeEmpty: {
            type: Boolean,
            default: false
        },
        hasSearch: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        hasCreateButton: {
            type: Boolean,
            default: false
        },
        isAutocomplete: {
            type: Boolean,
            default: false
        },
        selectedValues: {
            type: Array,
            default: () => []
        },
        showLoading: {
            type: Boolean,
            default: false
        },
        optionTitleFormatter: {
            type: Function,
            default: null
        },
        dropdownWidth: {
            type: String,
            default: null
        }
    },
    components: {
        AlphaBackgroundButton
    },
    data() {
        return {
            search: '',
            showed: false,
            highlightedIndex: null,
            zIndex: 950,
            popperInstance: null,
        }
    },
    emits: ['update:modelValue', 'update:search', 'click:create','update:toggle', 'reach-end', 'hide'],
    methods: {
        toggleSelect() {
            if (this.disabled) {
                return;
            }

            if (this.isAutocomplete) {
                this.$emit('update:toggle', !this.showed);

                return;
            }

            return this.showed ? this.hide() : this.show();
        },
        show() {
            if (this.showed) {
                return;
            }

            this.highlightedIndex = this.options.length > 0 ? 0 : null;
            this.showed = true;

            this.$nextTick(() => {
                if (!this.$refs.select) {
                    this.showed = false;
                    return;
                }

                if (this.$refs?.searchInput) {
                    this.$refs.searchInput.focus();
                }

                addEventListener('keydown', this.handleKeyDown);

                this.createPopper();
                this.addReachEndOnScrollListener();
            });
        },
        addReachEndOnScrollListener() {
            this.listScrollHandler = this.emitReachEndOnScroll.bind(this);
            this.$refs.list.addEventListener('scroll', this.listScrollHandler);
        },
        removeReachEndOnScrollListener() {
            if (!this.$refs.list) {
                return;
            }
            this.$refs.list.removeEventListener('scroll', this.listScrollHandler);
            this.listScrollHandler = null;
        },
        emitReachEndOnScroll(event) {
            const el = event.target;
            const threshold = 10;
            const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;

            if (atBottom) {
                this.$emit('reach-end');
            }
        },
        scrollIntoViewIfNeeded() {
            const option = this.$refs.list.children[this.highlightedIndex];
            if (option) {
                option.scrollIntoView({
                    block: 'nearest',
                    inline: 'nearest',
                    behavior: 'smooth'
                });
            }
        },
        handleKeyDown(event) {
            if (event.key === 'ArrowUp') {
                this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
                event.preventDefault();
                this.scrollIntoViewIfNeeded();
            } else if (event.key === 'ArrowDown') {
                this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.options.length - 1);
                event.preventDefault();
                this.scrollIntoViewIfNeeded();
            } else if (event.key === 'Enter') {
                if (this.options[this.highlightedIndex] === undefined) {
                    return;
                };
                this.$emit('update:modelValue', this.options[this.highlightedIndex]);
                this.hide();
            }
        },
        hide() {
            if (!this.showed) {
                return;
            }

            this.showed = false;
            this.destroyPopper();
            this.removeReachEndOnScrollListener();
            this.search = '';
            this.highlightedIndex = null;
            removeEventListener('keydown', this.handleKeyDown)
            this.$emit('hide');
        },
        createPopper() {
            if (this.$refs.select && this.$refs.options) {
                this.popperInstance = createPopper(this.$refs.select, this.$refs.options, {
                    placement: 'bottom-start',
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 2],
                            },
                        },
                        {
                            name: 'flip',
                            options: {
                                fallbackPlacements: ['top-start'],
                            },
                        },
                        {
                            name: 'preventOverflow',
                            options: {
                                boundary: 'clippingParents',
                            },
                        },
                        {
                            name: 'sameWidth',
                            enabled: true,
                            phase: 'beforeWrite',
                            requires: ['computeStyles'],
                            fn: ({ state }) => {
                                state.styles.popper.width = this.dropdownWidth ?? `${state.rects.reference.width}px`;
                            },
                            effect: ({ state }) => {
                                state.elements.popper.style.width = this.dropdownWidth ?? `${state.elements.reference.offsetWidth}px`;
                            },
                        },
                    ],
                });
            }
        },
        destroyPopper() {
            if (this.popperInstance) {
                this.popperInstance.destroy();
                this.popperInstance = null;
            }
        },
        findByPath(object, patch) {
            let parts = patch.split('.');

            if (parts.length === 1) {
                return object[parts[0]];
            }

            return this.findByPath(object[parts[0]], parts.slice(1).join('.'));
        },
        updateModelValue(option) {
            this.$emit('update:modelValue', option);
            this.hide();
        }
    },
    mounted() {
        let modalCount = document.getElementsByClassName('modal-backdrop').length

        if (modalCount > 0) {
            this.zIndex = 1000 + (modalCount * 10) + 7
        }
    },
    watch: {
        options() {
            setTimeout(() => {
                if (this.popperInstance) {
                    this.popperInstance.update();
                }
                if (this.options.length > 0) {
                    this.highlightedIndex = 0
                }
            }, 0)
        }
    },
    directives: {
        clickOutside: vClickOutside.directive
    },
    unmounted() {
        removeEventListener('keydown', this.handleKeyDown)
        this.destroyPopper();
        this.removeReachEndOnScrollListener();
    }
}
</script>
<style scoped>
.options-footer {
    border-top: 1px solid #ddd;
    padding: .5rem;
}
.select2 {
    width: 100%;
}
.select2-results__option {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
